"use client";

import React, { useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Clock,
  LayoutGrid,
  LayoutTemplate,
  Minus,
  Rows3,
  Settings,
  type LucideIcon,
} from "lucide-react";

export type PlatformProfile = {
  name: string;
  description: React.ReactNode;
  bestFor: React.ReactNode;
  weakestAt: string;
  lookElsewhere: string;
  icon: React.ReactNode;
  badgeClass: string;
  mockup?: string;
  link?: { href: string; label: string };
};

const slug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const TAB_BASE =
  "flex min-h-11 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap px-1.5 py-2.5 text-[11px] font-semibold transition-colors hover:bg-[#F7F9FE] hover:text-[#1D2C7F] focus-visible:outline-3 focus-visible:outline-offset-[5px] focus-visible:outline-[#7995E6] motion-reduce:transition-none max-md:rounded-[5px] max-md:border md:-mb-px md:min-h-[62px] md:gap-[5px] md:border-b-[3px] md:pb-5 md:pt-[18px] lg:gap-[9px] lg:text-sm";
const TAB_ACTIVE = "text-[#1D2C7F] max-md:border-accent max-md:bg-[#EEF2FC] md:border-b-accent";
const TAB_IDLE = "text-[#697080] max-md:border-[#E3E7EF] md:border-b-transparent";

export function PlatformProfiles({
  cards,
  header,
}: {
  cards: PlatformProfile[];
  header: React.ReactNode;
}) {
  const [selected, setSelected] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const activate = (index: number, focus = false) => {
    setSelected(index);
    if (focus) tabRefs.current[index]?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number;
    if (event.key === "ArrowRight") next = (index + 1) % cards.length;
    else if (event.key === "ArrowLeft") next = (index - 1 + cards.length) % cards.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = cards.length - 1;
    else return;
    event.preventDefault();
    activate(next, true);
  };

  return (
    <div>
      {header}

      <div
        role="tablist"
        aria-label="Digital signage platforms"
        className="mb-7 grid grid-cols-3 gap-1 md:mb-[39px] md:grid-cols-7 md:gap-0.5 md:border-b md:border-[#E3E7EF] lg:gap-1.5"
      >
        {cards.map((card, index) => {
          const active = index === selected;
          return (
            <button
              key={card.name}
              ref={node => {
                tabRefs.current[index] = node;
              }}
              type="button"
              role="tab"
              id={`profile-tab-${slug(card.name)}`}
              aria-controls={`profile-panel-${slug(card.name)}`}
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              onClick={() => activate(index)}
              onKeyDown={event => onKeyDown(event, index)}
              className={`${TAB_BASE} ${active ? TAB_ACTIVE : TAB_IDLE}`}
            >
              <VendorIcon
                card={card}
                className="h-5 w-5 rounded-[5px] lg:h-7 lg:w-7 lg:rounded-md"
              />
              {card.name}
            </button>
          );
        })}
      </div>

      {cards.map((card, index) => (
        <div
          key={card.name}
          role="tabpanel"
          id={`profile-panel-${slug(card.name)}`}
          aria-labelledby={`profile-tab-${slug(card.name)}`}
          tabIndex={0}
          hidden={index !== selected}
          className="grid items-center gap-4 focus-visible:outline-3 focus-visible:outline-offset-[5px] focus-visible:outline-[#7995E6] md:min-h-[475px] md:grid-cols-[1.05fr_1fr] md:gap-[30px] lg:gap-[65px]"
        >
          <div className="text-[#222A3B]">
            <h3 className="mb-[19px] flex items-center gap-[11px] text-[26px] leading-[1.25] tracking-[-0.8px] font-[650] md:text-[29px]">
              <VendorIcon card={card} className="h-10 w-10 rounded-xl" />
              {card.name}
            </h3>
            <p className="mb-[23px] text-[13px] leading-[1.85] text-[#4F5869] lg:text-sm">
              {card.description}
            </p>
            <dl className="m-0">
              <DetailRow tone="best" Icon={Check} label="Best for" value={card.bestFor} />
              <DetailRow tone="weak" Icon={Minus} label="Weakest at" value={card.weakestAt} />
              <DetailRow
                tone="else"
                Icon={ArrowUpRight}
                label="Look elsewhere if"
                value={card.lookElsewhere}
              />
            </dl>
            {card.link && (
              <Link
                href={card.link.href}
                className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
              >
                {card.link.label.replace(/\s*→$/, "")}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            )}
          </div>

          <div className="mx-auto w-full min-w-0 max-w-[480px] md:max-w-none">
            <Visual card={card} />
          </div>
        </div>
      ))}
    </div>
  );
}

function VendorIcon({ card, className = "" }: { card: PlatformProfile; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`flex flex-none items-center justify-center overflow-hidden ${card.badgeClass} ${className}`}
    >
      {card.icon}
    </span>
  );
}

const DETAIL_TONES = {
  best: "border-[#D7E8DE] bg-[#F3FAF6] text-[#27794D]",
  weak: "border-[#E9E2D1] bg-[#FCFAF4] text-[#A47725]",
  else: "border-[#DCE2ED] text-[#1D2C7F]",
};

function DetailRow({
  tone,
  Icon,
  label,
  value,
}: {
  tone: keyof typeof DETAIL_TONES;
  Icon: LucideIcon;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="grid gap-2 border-t border-[#E3E7EF] py-[18px] text-[13px] leading-[1.8] md:grid-cols-[132px_1fr] md:gap-3 md:py-5 md:text-xs lg:grid-cols-[153px_1fr] lg:text-[13px]">
      <dt className="flex items-center gap-2.5 font-semibold md:items-start">
        <span
          aria-hidden="true"
          className={`inline-flex h-[22px] w-[22px] flex-none items-center justify-center rounded-md border md:mt-px ${DETAIL_TONES[tone]}`}
        >
          <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
        </span>
        {label}
      </dt>
      <dd className="m-0 pl-8 text-[#5F6776] md:pl-0">{value}</dd>
    </div>
  );
}

const NAV_ICONS: LucideIcon[] = [LayoutGrid, Rows3, LayoutTemplate, Clock, Settings];

function Visual({ card }: { card: PlatformProfile }) {
  return card.mockup ? (
    <MockupStage name={card.name} src={card.mockup} />
  ) : (
    <DashboardStage name={card.name} />
  );
}

function MockupStage({ name, src }: { name: string; src: string }) {
  return (
    <div className="relative py-6 before:absolute before:inset-x-[-15px] before:inset-y-0 before:bg-[radial-gradient(ellipse,#E5ECFB_0%,#F3F6FC_53%,transparent_73%)] before:content-['']">
      <div className="relative aspect-[925/608] w-full overflow-hidden rounded-2xl border border-[#E0E5EF] bg-white shadow-[0_18px_48px_#18317312]">
        <Image
          src={src}
          alt={`Illustrative digital signage screen, not a ${name} product screenshot`}
          fill
          sizes="(min-width: 1280px) 600px, (min-width: 768px) 45vw, 100vw"
          className="object-cover"
        />
      </div>
    </div>
  );
}

function DashboardStage({ name }: { name: string }) {
  return (
    <div
      role="img"
      aria-label="Illustrative digital signage dashboard, not a product screenshot"
      className="relative min-h-[310px] pb-[66px] pt-6 before:absolute before:inset-x-[-15px] before:bottom-5 before:top-0 before:bg-[radial-gradient(ellipse,#E5ECFB_0%,#F3F6FC_53%,transparent_73%)] before:content-[''] md:min-h-[340px]"
    >
      <div className="relative grid min-h-[255px] grid-cols-[52px_1fr] overflow-hidden rounded-[9px] border border-[#E0E5EF] bg-white shadow-[0_18px_48px_#18317312]">
        <div className="flex flex-col items-center gap-[13px] border-r border-[#E8EDF4] bg-[#F7F9FC] px-[7px] py-[15px]">
          <span className="mb-[9px] mt-px max-w-10 overflow-hidden text-[6px] text-[#1D2C7F] font-[750]">
            {name}
          </span>
          {NAV_ICONS.map((NavIcon, index) => (
            <span
              key={index}
              className={`grid h-[23px] w-6 place-items-center rounded-sm ${
                index === 0 ? "bg-[#E8EDF9] text-[#1D2C7F]" : "text-[#A2ACC0]"
              }`}
            >
              <NavIcon className="h-3.5 w-3.5" strokeWidth={1.75} />
            </span>
          ))}
        </div>

        <div className="px-4 pb-[13px] pt-4">
          <div className="flex items-center justify-between text-[9px] text-[#59637B]">
            <span>Content library</span>
            <span className="grid h-[19px] w-[19px] place-items-center rounded-full bg-[#EDF1FA] text-[8px] text-[#687899]">
              A
            </span>
          </div>
          <div className="mb-[13px] mt-5 flex items-center justify-between gap-[7px]">
            <strong className="text-[9px] text-[#38445C] md:text-[7px] lg:text-[9px]">
              Your workplace, connected.
            </strong>
            <span className="whitespace-nowrap rounded-[3px] bg-[#1D2C7F] px-1.5 py-[5px] text-[6px] text-white">
              + Add content
            </span>
          </div>
          <div className="grid grid-cols-3 gap-[9px]">
            <Tile
              className="bg-[#1D2C7F] text-white"
              kicker="COMPANY UPDATE"
              foot="News that matters."
            >
              Stay
              <br />
              informed.
            </Tile>
            <Tile
              className="bg-[#F5EDDB] text-[#4B483B]"
              kicker="HEALTH & SAFETY"
              foot="Every day. Everywhere."
            >
              Stay
              <br />
              safe.
            </Tile>
            <Tile
              className="bg-[#E7EDF6] text-[#304664]"
              kicker="OUR PEOPLE"
              foot="One connected team."
            >
              Better
              <br />
              together.
            </Tile>
          </div>
          <div className="mt-[17px] flex justify-between text-[7px] text-[#8190A8]">
            <span>Latest content</span>
            <div className="flex gap-[5px]">
              <i className="block h-1 w-[29px] rounded-[2px] bg-[#E5EAF2]" />
              <i className="block h-1 w-[29px] rounded-[2px] bg-[#E5EAF2]" />
              <i className="block h-1 w-[29px] rounded-[2px] bg-[#E5EAF2]" />
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-[17px] right-6 h-[111px] w-[185px] rounded-[5px] bg-[#26304B] p-[5px] shadow-[0_10px_25px_#20366D17] after:absolute after:-bottom-1 after:left-[70px] after:h-1 after:w-[45px] after:rounded-b-[3px] after:bg-[#26304B] after:content-['']">
        <div className="relative flex h-full flex-col bg-[#EEF3FB] px-[15px] py-[13px]">
          <small className="text-[5px] tracking-[0.6px] text-[#64748E]">WORKPLACE UPDATE</small>
          <b className="mt-2.5 text-[17px] leading-[1.15] tracking-[-0.4px] text-[#1D2C7F]">
            Make every
            <br />
            message count.
          </b>
          <span className="absolute bottom-[15px] right-3.5 h-[26px] w-[26px] rounded-full bg-[#CBD7F0]" />
        </div>
      </div>
    </div>
  );
}

function Tile({
  className,
  kicker,
  foot,
  children,
}: {
  className: string;
  kicker: string;
  foot: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`flex min-h-[119px] flex-col items-start rounded px-[9px] py-3 ${className}`}>
      <small className="text-[4.5px] tracking-[0.5px]">{kicker}</small>
      <b className="mt-3.5 text-[clamp(13px,3.6vw,19px)] leading-[1.06] tracking-[-0.6px] font-[650] md:text-base lg:text-[20px]">
        {children}
      </b>
      <span className="mt-auto pt-2.5 text-[5px]">{foot}</span>
    </div>
  );
}
